import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { http, trackEvent } from "@/lib/api";
import { Card, PageTitle, btnPrimary, fieldCls } from "@/components/portal/ui";

export default function Progress() {
  const [logs, setLogs] = useState([]);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), bedtime: "", onset: "", wakings: "", longest_stretch: "", morning_wake: "", naps: "", comments: "" });
  const load = () => http.get("/portal/sleep-logs").then((r) => setLogs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const add = async () => {
    const hasData = ["bedtime", "onset", "longest_stretch", "morning_wake", "comments"].some((k) => String(form[k] || "").trim())
      || form.wakings !== "" || form.naps !== "";
    if (!hasData) { toast.error("Add at least one detail before saving."); return; }
    try {
      await http.post("/portal/sleep-logs", { ...form, wakings: form.wakings ? Number(form.wakings) : null, naps: form.naps ? Number(form.naps) : null });
      toast.success("Logged"); trackEvent("sleep_tracker_used", { source: "progress" }); load();
      setForm((f) => ({ ...f, bedtime: "", onset: "", wakings: "", longest_stretch: "", morning_wake: "", naps: "", comments: "" }));
    } catch (e) { toast.error("Could not save"); }
  };

  const chart = [...logs].reverse().map((l) => ({ date: l.date?.slice(5), wakings: l.wakings ?? null }));

  return (
    <div>
      <PageTitle eyebrow="Free · Student Account" title="Sleep Observation Tracker" sub="Document and review your child's sleep patterns, including nighttime waking, naps, bedtime, and daily observations. Your records remain available as you continue your studies." />
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm text-[#6B665E] mb-2">Date</label><input type="date" className={fieldCls} value={form.date} onChange={(e) => set("date", e.target.value)} /></div>
            <div><label className="block text-sm text-[#6B665E] mb-2">Night wakings</label><input data-testid="log-wakings" type="number" className={fieldCls} value={form.wakings} onChange={(e) => set("wakings", e.target.value)} /></div>
            <div><label className="block text-sm text-[#6B665E] mb-2">Bedtime</label><input className={fieldCls} placeholder="8:00pm" value={form.bedtime} onChange={(e) => set("bedtime", e.target.value)} /></div>
            <div><label className="block text-sm text-[#6B665E] mb-2">Asleep by</label><input className={fieldCls} placeholder="8:20pm" value={form.onset} onChange={(e) => set("onset", e.target.value)} /></div>
            <div><label className="block text-sm text-[#6B665E] mb-2">Longest stretch</label><input className={fieldCls} placeholder="5h" value={form.longest_stretch} onChange={(e) => set("longest_stretch", e.target.value)} /></div>
            <div><label className="block text-sm text-[#6B665E] mb-2">Morning wake</label><input className={fieldCls} placeholder="6:30am" value={form.morning_wake} onChange={(e) => set("morning_wake", e.target.value)} /></div>
            <div><label className="block text-sm text-[#6B665E] mb-2">Naps</label><input type="number" className={fieldCls} value={form.naps} onChange={(e) => set("naps", e.target.value)} /></div>
          </div>
          <div><label className="block text-sm text-[#6B665E] mb-2">Notes</label><textarea rows={2} className={fieldCls} value={form.comments} onChange={(e) => set("comments", e.target.value)} /></div>
          <button data-testid="log-save" onClick={add} className={btnPrimary}>Add entry</button>
        </Card>

        <Card className="p-6">
          <div className="eyebrow text-[#8A998C] mb-4">Night wakings trend</div>
          {chart.length > 1 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E1D8" />
                <XAxis dataKey="date" stroke="#6B665E" fontSize={12} />
                <YAxis stroke="#6B665E" fontSize={12} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="wakings" stroke="#9F634E" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <p className="text-[#6B665E] text-sm">Add a few entries to see your trend.</p>}
        </Card>
      </div>

      <Card className="p-6 mt-6">
        <div className="eyebrow text-[#8A998C] mb-4">Recent entries</div>
        {logs.length === 0 ? <p className="text-[#6B665E] text-sm">No entries yet.</p> : (
          <div className="space-y-2">
            {logs.map((l) => (
              <div key={l.id} className="flex items-center justify-between border-b border-[#F0ECE4] py-2 text-sm">
                <span className="text-[#1A1918]">{l.date}</span>
                <span className="text-[#6B665E]">{l.wakings ?? "–"} wakings · longest {l.longest_stretch || "–"} · {l.naps ?? "–"} naps</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
